export type FeatureRequestWorkflowStatus =
  | "new"
  | "summarized"
  | "go"
  | "no-go"
  | "spec_created"
  | "plan_created"
  | "in_build"
  | "done"
  | "blocked";

export type FeatureScopeGuess = "small" | "medium" | "large";

export interface FeatureAutomationInput {
  summary: string;
  details: string | null;
  route: string;
  reporterRole: "coach" | "leader" | "admin";
}

export interface FeatureAutomationSummaryDraft {
  automationSummary: string;
  automationRecommendation: string;
  affectedSurfaces: string[];
  scopeGuess: FeatureScopeGuess;
  problemFraming: string;
}

export interface FeatureAutomationArtifactsDraft {
  specMarkdown: string;
  planMarkdown: string;
}

export interface FeatureGithubIssueDraft {
  title: string;
  body: string;
  labels: string[];
}

function normalizeText(value: string | null | undefined) {
  return (value ?? "").trim();
}

function titleCaseToken(value: string) {
  return value
    .split(/[-_]/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function routeToSurface(route: string): string[] {
  const normalizedRoute = normalizeText(route) || "/";
  const parts = normalizedRoute.split("/").filter(Boolean);

  if (parts.length === 0) return ["Home"];

  return parts.slice(0, 3).map(titleCaseToken);
}

function inferScopeGuess(input: FeatureAutomationInput): FeatureScopeGuess {
  const combined = `${input.summary} ${input.details ?? ""} ${input.route}`.toLowerCase();

  if (/(automation|pipeline|system|workflow|across|every|all surfaces|github|spec|plan)/.test(combined)) {
    return "large";
  }

  if (/(review|queue|internal|approval|feature request|product feedback|suggest feature)/.test(combined)) {
    return "medium";
  }

  return "small";
}

function inferRecommendation(input: FeatureAutomationInput, scopeGuess: FeatureScopeGuess) {
  if (scopeGuess === "large") {
    return `Go if this should become a deliberate internal workflow. The request touches multiple steps and should stay spec-first before any build work starts.`;
  }

  if (scopeGuess === "medium") {
    return `Go if this improves the core internal review loop. The request is bounded enough to plan now, but it still needs a spec-first path with a plan before implementation.`;
  }

  return `Go if this solves a real single-surface pain point. The build should still enter the harness through a lightweight spec-first sequence and plan.`;
}

export function buildFeatureAutomationFallback(input: FeatureAutomationInput): FeatureAutomationSummaryDraft {
  const scopeGuess = inferScopeGuess(input);
  const surfaces = routeToSurface(input.route);
  const summaryDetail = normalizeText(input.details);

  return {
    automationSummary: summaryDetail
      ? `${input.summary.trim()} This was submitted from ${input.route} by a ${input.reporterRole} and includes extra context about the workflow pain.`
      : `${input.summary.trim()} This was submitted from ${input.route} by a ${input.reporterRole}.`,
    automationRecommendation: inferRecommendation(input, scopeGuess),
    affectedSurfaces: surfaces,
    scopeGuess,
    problemFraming: summaryDetail
      ? `The current product flow does not cover this need cleanly: ${summaryDetail}`
      : `The current product flow likely has a gap around: ${input.summary.trim()}`,
  };
}

export function normalizeFeatureAutomationSummaryDraft(
  value: Partial<FeatureAutomationSummaryDraft> | null | undefined,
  input: FeatureAutomationInput,
): FeatureAutomationSummaryDraft {
  const fallback = buildFeatureAutomationFallback(input);
  const affectedSurfaces = Array.isArray(value?.affectedSurfaces)
    ? value?.affectedSurfaces.map((item) => normalizeText(String(item))).filter(Boolean)
    : [];

  return {
    automationSummary: normalizeText(value?.automationSummary) || fallback.automationSummary,
    automationRecommendation: normalizeText(value?.automationRecommendation) || fallback.automationRecommendation,
    affectedSurfaces: affectedSurfaces.length ? affectedSurfaces : fallback.affectedSurfaces,
    scopeGuess: value?.scopeGuess === "small" || value?.scopeGuess === "medium" || value?.scopeGuess === "large"
      ? value.scopeGuess
      : fallback.scopeGuess,
    problemFraming: normalizeText(value?.problemFraming) || fallback.problemFraming,
  };
}

export function buildFeatureArtifactsFallback(
  input: FeatureAutomationInput,
  summaryDraft: FeatureAutomationSummaryDraft,
): FeatureAutomationArtifactsDraft {
  const title = input.summary.trim();
  const affectedSurfaces = summaryDraft.affectedSurfaces.join(", ");

  return {
    specMarkdown: `# ${title} Spec Draft

## Goal

${summaryDraft.problemFraming}

## Request Summary

${summaryDraft.automationSummary}

## Recommendation

${summaryDraft.automationRecommendation}

## Affected Surfaces

- ${affectedSurfaces || "Unknown"}

## Scope

${summaryDraft.scopeGuess}

## Startup Harness Rule

This request was approved for spec-first work. It must not jump directly into implementation.
`,
    planMarkdown: `# ${title} Plan Draft

## Goal

Turn the approved feature request into a bounded implementation plan.

## Suggested Sequence

1. Confirm the affected surfaces: ${affectedSurfaces || "Unknown"}
2. Break the work into one bounded first slice
3. Write focused tests for the first slice
4. Implement the smallest durable unit
5. Verify and review before moving to the next slice

## Scope Guess

${summaryDraft.scopeGuess}

## Notes

Use the generated spec draft as the starting point for the full implementation plan.
`,
  };
}

export function normalizeFeatureArtifactsDraft(
  value: Partial<FeatureAutomationArtifactsDraft> | null | undefined,
  input: FeatureAutomationInput,
  summaryDraft: FeatureAutomationSummaryDraft,
): FeatureAutomationArtifactsDraft {
  const fallback = buildFeatureArtifactsFallback(input, summaryDraft);

  return {
    specMarkdown: normalizeText(value?.specMarkdown) || fallback.specMarkdown,
    planMarkdown: normalizeText(value?.planMarkdown) || fallback.planMarkdown,
  };
}

export function buildFeatureGithubIssueDraft(input: {
  request: {
    id: string;
    summary: string;
    details: string | null;
    route: string;
    reporter_role: "coach" | "leader" | "admin";
    reporter_user_id: string;
    created_at: string;
  };
  run: {
    id: string;
    feature_request_id: string;
    status: string;
    automation_summary: string | null;
    automation_recommendation: string | null;
    affected_surfaces: string[] | null;
    scope_guess: FeatureScopeGuess | null;
    problem_framing: string | null;
    github_issue_number: number | null;
    github_issue_url: string | null;
    github_sync_status: string | null;
    github_sync_error: string | null;
  };
  summaryDraft?: FeatureAutomationSummaryDraft;
}): FeatureGithubIssueDraft {
  const affectedSurfaces = Array.isArray(input.run.affected_surfaces) ? input.run.affected_surfaces : [];
  const summaryDraft = input.summaryDraft ?? buildFeatureAutomationFallback({
    summary: input.request.summary,
    details: input.request.details,
    route: input.request.route,
    reporterRole: input.request.reporter_role,
  });
  const statusLine = [
    `spec: ${input.run.status === "spec_created" ? "created" : "pending"}`,
    `plan: ${input.run.status === "plan_created" ? "created" : "pending"}`,
    `build: ${input.run.status === "in_build" ? "in build" : "pending"}`,
  ].join(" | ");

  return {
    title: input.request.summary.trim(),
    labels: ["feature-intake", "feature-new"],
    body: `# Feature Request Intake

## Metadata

- feature_request_id: ${input.request.id}
- run_id: ${input.run.id}
- submitted_route: ${input.request.route}
- reporter_role: ${input.request.reporter_role}
- reporter_user_id: ${input.request.reporter_user_id}
- created_at: ${input.request.created_at}

## Original Request

### Summary

${input.request.summary}

### Details

${input.request.details ?? "none"}

## Automation Summary

${input.run.automation_summary ?? summaryDraft.automationSummary}

## Recommendation

${input.run.automation_recommendation ?? summaryDraft.automationRecommendation}

## Affected Surfaces

${affectedSurfaces.length ? affectedSurfaces.map((surface) => `- ${surface}`).join("\n") : "- Unknown"}

## Scope Guess

${input.run.scope_guess ?? summaryDraft.scopeGuess}

## Problem Framing

${input.run.problem_framing ?? summaryDraft.problemFraming}

## Decision Instructions

Apply \`feature-go\` or \`feature-no-go\` in GitHub. Do not copy this into chat.

## Harness Status

${statusLine}

## GitHub Sync

- github_issue_number: ${input.run.github_issue_number ?? "pending"}
- github_issue_url: ${input.run.github_issue_url ?? "pending"}
- github_sync_status: ${input.run.github_sync_status ?? "pending"}
- github_sync_error: ${input.run.github_sync_error ?? "none"}
`,
  };
}
